// Cuentas de la sección Estadísticas: junta los días que devuelve loadStats (src/stats.js)
// en totales, rankings y la grilla de horarios que muestran los gráficos.

export const WEEKDAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
export const WEEKDAYS_SHORT = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

// getDay() empieza en domingo; la grilla empieza en lunes.
const weekday = (date) => (date.getDay() + 6) % 7;

const sumKey = (days, key) => days.reduce((n, d) => n + (d[key] || 0), 0);

const sumMaps = (days, key) => {
    const out = {};
    days.forEach((d) => Object.entries(d[key] || {}).forEach(([id, n]) => {
        out[id] = (out[id] || 0) + (Number(n) || 0);
    }));
    return out;
}

const ranking = (map, products) => Object.entries(map)
    .map(([id, n]) => ({ product: products.find((p) => p.id === id), n }))
    .filter((r) => r.product && r.n > 0)
    .sort((a, b) => b.n - a.n);

export const ratio = (part, whole) => whole > 0 ? part / whole : 0;

export const summarize = (days, products) => {
    const t = {};
    ["visitas", "visitasQr", "exploraron", "carritos", "pedidos", "pedidosConTotal", "importe", "retiro", "envio", "clicsWhatsapp"]
        .forEach((key) => { t[key] = sumKey(days, key); });

    const views = sumMaps(days, "vistas");
    const adds = sumMaps(days, "agregados");
    const sent = sumMaps(days, "enviados");

    // Horarios: filas de lunes a domingo, columnas de 0 a 23 h.
    const heat = { visitas: WEEKDAYS.map(() => Array(24).fill(0)), pedidos: WEEKDAYS.map(() => Array(24).fill(0)) };
    days.forEach((d) => {
        const row = weekday(d.date);
        Object.entries(d.horas).forEach(([h, n]) => { if (heat.visitas[row][h] !== undefined) heat.visitas[row][h] += Number(n) || 0; });
        Object.entries(d.horasPedidos).forEach(([h, n]) => { if (heat.pedidos[row][h] !== undefined) heat.pedidos[row][h] += Number(n) || 0; });
    });

    const categories = {};
    Object.entries(views).forEach(([id, n]) => {
        const product = products.find((p) => p.id === id);
        if (product?.category) categories[product.category] = (categories[product.category] || 0) + n;
    });

    // Productos que se abren seguido pero casi nadie agrega: puede ser el precio o la foto.
    const minViews = Math.max(5, Math.round(t.visitas * 0.05));
    const lowAdd = ranking(views, products)
        .filter((r) => r.n >= minViews && !r.product.agotado)
        .map((r) => ({ ...r, added: adds[r.product.id] || 0, rate: Math.min(1, ratio(adds[r.product.id] || 0, r.n)) }))
        .filter((r) => r.rate < 0.15)
        .slice(0, 5);

    return {
        totals: t,
        conversion: ratio(t.pedidos, t.visitas),
        ticket: t.pedidosConTotal ? t.importe / t.pedidosConTotal : 0,
        // Si hay pedidos sin total (precios "a consultar"), el total estimado los cuenta con el ticket promedio.
        estimated: t.pedidosConTotal ? t.importe + (t.pedidos - t.pedidosConTotal) * (t.importe / t.pedidosConTotal) : 0,
        abandoned: Math.max(0, t.carritos - t.pedidos),
        byDay: days.map((d) => ({ date: d.date, visitas: d.visitas, pedidos: d.pedidos })),
        heat,
        topViewed: ranking(views, products),
        topAdded: ranking(adds, products),
        topSent: ranking(sent, products),
        lowAdd,
        categories: Object.entries(categories).map(([name, n]) => ({ name, n })).sort((a, b) => b.n - a.n),
        hasViews: Object.keys(views).length > 0
    };
}

// Celda, día y hora con más movimiento de una grilla de horarios.
export const peaks = (grid) => {
    let best = { row: 0, hour: 0, n: 0 };
    grid.forEach((row, r) => row.forEach((n, h) => { if (n > best.n) best = { row: r, hour: h, n }; }));
    const byDay = grid.map((row) => row.reduce((a, b) => a + b, 0));
    const byHour = Array.from({ length: 24 }, (_, h) => grid.reduce((n, row) => n + row[h], 0));
    const topDay = byDay.indexOf(Math.max(...byDay));
    const topHour = byHour.indexOf(Math.max(...byHour));
    return { cell: best, topDay: byDay[topDay] ? topDay : -1, topHour: byHour[topHour] ? topHour : -1, byDay, byHour };
}

export const hourRange = (h) => `${h} a ${(h + 1) % 24} h`;

// Datos inventados para la vista previa borrosa del plan Gratis.
export const demoSummary = () => {
    const rand = (seed) => {
        const x = Math.sin(seed * 9301 + 49297) * 233280;
        return x - Math.floor(x);
    };
    const products = ["Muzzarella", "Napolitana", "Fugazzeta", "Empanadas x6", "Faina", "Cerveza IPA", "Flan casero", "Calabresa"]
        .map((name, i) => ({ id: "d" + i, name, category: i < 4 ? "Pizzas" : i < 6 ? "Para compartir" : "Postres" }));
    const today = new Date();
    const days = Array.from({ length: 30 }, (_, i) => {
        const date = new Date(today);
        date.setDate(today.getDate() - (29 - i));
        const weekend = [0, 5, 6].includes(date.getDay());
        const visitas = Math.round((weekend ? 70 : 38) + rand(i) * 30);
        const pedidos = Math.round(visitas * (0.12 + rand(i + 50) * 0.08));
        const horas = {};
        const horasPedidos = {};
        [12, 13, 20, 21, 22].forEach((h, k) => {
            horas[h] = Math.round(visitas * [0.15, 0.12, 0.22, 0.25, 0.14][k]);
            horasPedidos[h] = Math.round(pedidos * [0.1, 0.1, 0.3, 0.35, 0.15][k]);
        });
        const vistas = Object.fromEntries(products.map((p, k) => [p.id, Math.round(visitas * (0.5 - k * 0.05) * (0.8 + rand(i + k) * 0.4))]));
        const agregados = Object.fromEntries(products.map((p, k) => [p.id, Math.round(vistas[p.id] * (k === 2 ? 0.05 : 0.35))]));
        const enviados = Object.fromEntries(products.map((p, k) => [p.id, Math.round(agregados[p.id] * 0.7)]));
        return {
            date, visitas, pedidos, visitasQr: Math.round(visitas * 0.62), exploraron: Math.round(visitas * 0.7),
            carritos: Math.round(pedidos * 1.6), pedidosConTotal: pedidos, importe: pedidos * 14500,
            retiro: Math.round(pedidos * 0.4), envio: Math.round(pedidos * 0.6), clicsWhatsapp: Math.round(visitas * 0.08),
            vistas, agregados, enviados, horas, horasPedidos
        };
    });
    return { summary: summarize(days, products), previous: summarize(days.slice(0, 15), products) };
}
